import {
    CSSProperties,
    FC,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { AlertTriangle, Trash2 } from 'lucide-react';

import { BulkAutoPriceControls, BulkOfferMassEdit } from './BulkOfferMassEdit';
import { BulkOfferTableRow } from './BulkOfferTableRow';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import { OfferDraft, OfferProperty } from '../../../../types';
import { getProductsCountText } from '../../../../utils';
import {
    isOfferDraftValid,
    OfferDraftFormValues,
    resolveOfferPropertiesForProduct,
} from '../__shared/offerDraft';

interface Props {
    queuedOffers: OfferDraft[];
    offerProperties: OfferProperty[];
    editorProperties: OfferProperty[];
    contextControls: ReactNode;
    productSearch: ReactNode;
    headerExtra?: ReactNode;
    autoPriceControls: BulkAutoPriceControls | null;
    processing: boolean;
    submitDisabled: boolean;
    onRemove: (id: string) => void;
    onDuplicate: (id: string) => void;
    onPhotoUploadingChange: (draftId: string, uploading: boolean) => void;
    onFieldChange: <Key extends keyof OfferDraftFormValues>(
        draftId: string,
        field: Key,
        value: OfferDraftFormValues[Key],
    ) => void;
    onBulkRemove: (draftIds: string[]) => void;
    onBulkPropertyApply: (
        draftIds: string[],
        slug: string,
        value: string,
    ) => void;
    onBulkManualPriceChange: (price: string) => void;
    onBulkPriceApply: (draftIds: string[], price: string) => void;
    onSubmit: () => void;
}

const compareOfferProperties = (left: OfferProperty, right: OfferProperty) =>
    left.position - right.position || left.id - right.id;

export const BulkOffersQueue: FC<Props> = ({
    queuedOffers,
    offerProperties,
    editorProperties,
    contextControls,
    productSearch,
    headerExtra,
    autoPriceControls,
    processing,
    submitDisabled,
    onRemove,
    onDuplicate,
    onPhotoUploadingChange,
    onFieldChange,
    onBulkRemove,
    onBulkPropertyApply,
    onBulkManualPriceChange,
    onBulkPriceApply,
    onSubmit,
}) => {
    const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>([]);

    useEffect(() => {
        setSelectedDraftIds((currentIds) => {
            const availableIds = currentIds.filter((draftId) =>
                queuedOffers.some((offer) => offer.draft_id === draftId),
            );

            return availableIds.length === currentIds.length
                ? currentIds
                : availableIds;
        });
    }, [queuedOffers]);

    const visibleProperties = useMemo(() => {
        const productTypeIds = new Set(
            queuedOffers
                .map(
                    (offer) =>
                        offer.product?.product_type_id ?? offer.product_type_id,
                )
                .filter((productTypeId): productTypeId is number =>
                    Boolean(productTypeId),
                ),
        );
        const relevantProperties = [
            ...offerProperties.filter((property) =>
                productTypeIds.has(property.product_type_id),
            ),
            ...editorProperties,
        ];

        const propertiesBySlug = relevantProperties.reduce<
            Map<string, OfferProperty>
        >((properties, property) => {
            const existingProperty = properties.get(property.slug);

            if (
                !existingProperty ||
                compareOfferProperties(property, existingProperty) < 0
            ) {
                properties.set(property.slug, property);
            }

            return properties;
        }, new Map());

        return Array.from(propertiesBySlug.values()).sort(
            compareOfferProperties,
        );
    }, [editorProperties, offerProperties, queuedOffers]);
    const massEditProperties = useMemo(() => {
        const targetIds = new Set(
            selectedDraftIds.length > 0
                ? selectedDraftIds
                : queuedOffers.map((offer) => offer.draft_id),
        );
        const targetOffers = queuedOffers.filter((offer) =>
            targetIds.has(offer.draft_id),
        );

        return visibleProperties
            .map((property) => {
                const matchingProperties = targetOffers
                    .map((offer) =>
                        resolveOfferPropertiesForProduct(
                            offer.product,
                            offerProperties,
                        ).find((candidate) => candidate.slug === property.slug),
                    )
                    .filter((candidate): candidate is OfferProperty =>
                        Boolean(candidate),
                    );

                if (matchingProperties.length === 0) return null;
                if (property.kind === 'string') return property;
                if (property.kind !== 'collection') return null;

                const firstProperty = matchingProperties[0];
                const sharedOptions =
                    firstProperty.offer_property_options.filter((option) =>
                        matchingProperties.every((candidate) =>
                            candidate.offer_property_options.some(
                                (candidateOption) =>
                                    candidateOption.slug === option.slug,
                            ),
                        ),
                    );

                return sharedOptions.length > 0
                    ? {
                          ...property,
                          offer_property_options: sharedOptions,
                      }
                    : null;
            })
            .filter((property): property is OfferProperty => Boolean(property));
    }, [offerProperties, queuedOffers, selectedDraftIds, visibleProperties]);
    const baseColumnsWidth = 784;
    const propertyColumnsWidth = visibleProperties.length * 120;
    const columnsCount = 6 + visibleProperties.length;
    const columnsGapWidth = (columnsCount - 1) * 12;
    const horizontalPaddingWidth = 32;
    const tableMinWidth =
        baseColumnsWidth +
        propertyColumnsWidth +
        columnsGapWidth +
        horizontalPaddingWidth;
    const propertyColumns = visibleProperties.length
        ? ` repeat(${visibleProperties.length}, minmax(120px, 0.9fr))`
        : '';
    const tableGridStyle = {
        '--bulk-offers-grid': `36px minmax(220px, 1.6fr) 88px 116px${propertyColumns} minmax(220px, 1fr) 104px`,
        '--bulk-offers-min-width': `${tableMinWidth}px`,
    } as CSSProperties;

    const invalidOffersCount = queuedOffers.filter(
        (offer) => !isOfferDraftValid(offer, offerProperties),
    ).length;
    const totalValue = queuedOffers.reduce((total, offer) => {
        const amount = Number.parseInt(offer.amount, 10);
        const price = Number.parseFloat(offer.price);

        if (!Number.isFinite(amount) || !Number.isFinite(price)) return total;

        return total + amount * price;
    }, 0);
    const formattedTotalValue = `${new Intl.NumberFormat('uk-UA', {
        maximumFractionDigits: 0,
    }).format(totalValue)} ₴`;

    const allSelected =
        queuedOffers.length > 0 &&
        selectedDraftIds.length === queuedOffers.length;
    const selectAllState = allSelected
        ? true
        : selectedDraftIds.length > 0
          ? 'indeterminate'
          : false;
    const targetDraftIds =
        selectedDraftIds.length > 0
            ? selectedDraftIds
            : queuedOffers.map((offer) => offer.draft_id);

    const toggleAllDrafts = () =>
        setSelectedDraftIds(
            allSelected ? [] : queuedOffers.map((offer) => offer.draft_id),
        );

    const toggleDraft = (draftId: string, selected: boolean) =>
        setSelectedDraftIds((currentIds) =>
            selected
                ? [...currentIds, draftId]
                : currentIds.filter((currentId) => currentId !== draftId),
        );

    return (
        <section className="min-w-0 space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-bold sm:text-3xl">
                    Створити пропозиції
                </h1>
                {headerExtra && (
                    <div className="w-full sm:w-auto">{headerExtra}</div>
                )}
            </div>

            <div className="min-w-0 space-y-3">
                {contextControls}

                <div className="overflow-hidden rounded-2xl border bg-muted/30">
                    <div
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4"
                        data-testid="bulk-offers-toolbar"
                    >
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="select-all-drafts"
                                checked={selectAllState}
                                disabled={queuedOffers.length === 0}
                                aria-label="Обрати всі товари"
                                onCheckedChange={toggleAllDrafts}
                            />
                            <label
                                htmlFor="select-all-drafts"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Обрати все
                            </label>
                        </div>
                        <span className="text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">
                                {selectedDraftIds.length}
                            </span>{' '}
                            / {getProductsCountText(queuedOffers.length)}
                        </span>
                        {selectedDraftIds.length > 0 && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-8 gap-1.5 px-2 text-sm text-destructive hover:text-destructive"
                                onClick={() => onBulkRemove(selectedDraftIds)}
                            >
                                <Trash2 className="h-4 w-4" />
                                Видалити ({selectedDraftIds.length})
                            </Button>
                        )}

                        <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-2">
                            {invalidOffersCount > 0 && (
                                <Badge
                                    variant="destructive"
                                    className="gap-1"
                                    data-testid="bulk-offers-invalid-count"
                                >
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    {invalidOffersCount}
                                </Badge>
                            )}
                            <span className="text-sm font-semibold">
                                Всього: {formattedTotalValue}
                            </span>
                            <Button
                                type="button"
                                variant="success"
                                className="h-8 text-sm"
                                onClick={onSubmit}
                                disabled={
                                    processing ||
                                    queuedOffers.length === 0 ||
                                    submitDisabled
                                }
                            >
                                Створити пропозиції ({queuedOffers.length})
                            </Button>
                        </div>
                    </div>

                    {productSearch}

                    <BulkOfferMassEdit
                        properties={massEditProperties}
                        selectedCount={selectedDraftIds.length}
                        totalCount={queuedOffers.length}
                        autoPrice={autoPriceControls}
                        onPropertyApply={(slug, value) =>
                            onBulkPropertyApply(targetDraftIds, slug, value)
                        }
                        onManualPriceChange={onBulkManualPriceChange}
                        onPriceApply={(price) =>
                            onBulkPriceApply(targetDraftIds, price)
                        }
                    />
                </div>

                <div
                    className="max-w-full overflow-x-auto rounded-2xl border"
                    data-testid="bulk-offers-table"
                    style={tableGridStyle}
                >
                    <div
                        className="grid min-w-[var(--bulk-offers-min-width)] [grid-template-columns:var(--bulk-offers-grid)] gap-2 bg-muted/60 px-3 py-2.5 text-xs font-semibold text-muted-foreground md:gap-3 md:px-4"
                        data-testid="bulk-offers-table-header"
                    >
                        <div />
                        <div>Товар</div>
                        <div>Кількість</div>
                        <div>Ціна</div>
                        {visibleProperties.map((property) => (
                            <div key={property.slug}>{property.name}</div>
                        ))}
                        <div>Фото</div>
                        <div className="text-right">Дії</div>
                    </div>

                    <div className="divide-y">
                        {queuedOffers.length === 0 ? (
                            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                Знайдіть товар вище, щоб додати першу
                                пропозицію.
                            </div>
                        ) : (
                            queuedOffers.map((offer) => (
                                <BulkOfferTableRow
                                    key={offer.draft_id}
                                    offer={offer}
                                    offerProperties={offerProperties}
                                    tableProperties={visibleProperties}
                                    selected={selectedDraftIds.includes(
                                        offer.draft_id,
                                    )}
                                    onSelectedChange={(selected) =>
                                        toggleDraft(offer.draft_id, selected)
                                    }
                                    onRemove={() => onRemove(offer.draft_id)}
                                    onDuplicate={() =>
                                        onDuplicate(offer.draft_id)
                                    }
                                    onPhotoUploadingChange={(uploading) =>
                                        onPhotoUploadingChange(
                                            offer.draft_id,
                                            uploading,
                                        )
                                    }
                                    autoPriceEnabled={Boolean(
                                        autoPriceControls?.enabled,
                                    )}
                                    onFieldChange={(field, value) =>
                                        onFieldChange(
                                            offer.draft_id,
                                            field,
                                            value,
                                        )
                                    }
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
